import { isTruthy } from '@ardoq/common-helpers';
import {
  CellRange,
  GridApi,
  ProcessDataFromClipboardParams,
} from 'ag-grid-community';
import { OnPasteRow, PastedData, ProcessDataFromClipboard, Row } from './types';

export const toProcessDataFromClipboard =
  <RowData, RowId extends string = string>(
    processDataFromClipboard: ProcessDataFromClipboard<RowData>
  ) =>
  (params: ProcessDataFromClipboardParams<Row<RowData, RowId>>) => {
    const columnIds = params.api
      .getAllDisplayedColumns()
      .map(col => col.getColId());

    const { pasteRows, selectionBounds } = prepPasteData(
      params.data,
      columnIds,
      params.api.getCellRanges(),
      getAllDisplayedRows<RowData, RowId>(params.api)
    );

    const processedRows = pasteRows
      ? processDataFromClipboard(pasteRows)
      : null;

    if (!processedRows || !selectionBounds) {
      return null;
    }

    setGridSelection<RowData, RowId>(params.api, selectionBounds, columnIds);

    return get2DArrayFromClipboardData<RowData>(
      processedRows,
      columnIds as (keyof RowData)[],
      selectionBounds
    );
  };

const prepPasteData = <RowData, RowId extends string = string>(
  data: string[][],
  columnIds: string[],
  cellRanges: CellRange[] | null,
  rows: Row<RowData, RowId>[]
) => {
  const initialSelectionBounds = getSelectionBounds(cellRanges, columnIds);

  if (!initialSelectionBounds) {
    return { pasteRows: null, selectionBounds: null };
  }

  const { repeatedData, selectionBounds } = repeatDataToFillSelection(
    data,
    initialSelectionBounds
  );

  return {
    pasteRows: getPasteRows<RowData, RowId>(
      repeatedData,
      selectionBounds,
      columnIds,
      rows
    ),
    selectionBounds,
  };
};

const getAllDisplayedRows = <RowData, RowId extends string = string>(
  api: GridApi<Row<RowData, RowId>>
): Row<RowData, RowId>[] => {
  const rows: Row<RowData, RowId>[] = [];
  api.forEachNodeAfterFilterAndSort(node => {
    if (node.data) {
      rows.push(node.data);
    }
  });
  return rows;
};

const setGridSelection = <RowData, RowId extends string = string>(
  api: GridApi<Row<RowData, RowId>>,
  selectionBounds: SelectionBounds,
  columnIds: string[]
) => {
  api.clearCellSelection();
  api.addCellRange({
    rowStartIndex: selectionBounds.startRowIndex,
    rowEndIndex: selectionBounds.endRowIndex,
    columnStart: columnIds[selectionBounds.startColumnIndex],
    columnEnd: columnIds[selectionBounds.endColumnIndex],
  });
};

export const repeatDataToFillSelection = (
  data: string[][],
  initialSelectionBounds: SelectionBounds
) => {
  if (!data.length) {
    return { repeatedData: data, selectionBounds: initialSelectionBounds };
  }

  const repeatedData: string[][] = [];
  const dataRowCount = data.length;
  const dataColCount = data[0].length;
  const selectionRowCount =
    initialSelectionBounds.endRowIndex -
    initialSelectionBounds.startRowIndex +
    1;
  const selectionColCount =
    initialSelectionBounds.endColumnIndex -
    initialSelectionBounds.startColumnIndex +
    1;

  const rowsRepeated = Math.max(
    Math.floor(selectionRowCount / dataRowCount),
    1
  );
  const colsRepeated = Math.max(
    Math.floor(selectionColCount / dataColCount),
    1
  );

  for (let i = 0; i < rowsRepeated; i++) {
    for (const row of data) {
      const newRow: string[] = [];
      for (let j = 0; j < colsRepeated; j++) {
        newRow.push(...row);
      }
      repeatedData.push(newRow);
    }
  }

  const selectionBounds = {
    startRowIndex: initialSelectionBounds.startRowIndex,
    endRowIndex: initialSelectionBounds.startRowIndex + repeatedData.length - 1,
    startColumnIndex: initialSelectionBounds.startColumnIndex,
    endColumnIndex:
      initialSelectionBounds.startColumnIndex + dataColCount * colsRepeated - 1,
  };

  return { repeatedData, selectionBounds };
};

export const get2DArrayFromClipboardData = <RowData>(
  clipboardData: OnPasteRow<RowData>[],
  columnIds: (keyof RowData)[],
  selectionBounds: SelectionBounds
): string[][] => {
  const selectedColumnIds = columnIds.slice(
    selectionBounds.startColumnIndex,
    selectionBounds.endColumnIndex + 1
  );

  return clipboardData.map(row =>
    selectedColumnIds.map(colId => row.pasted[colId] ?? '')
  );
};

export const getPasteRows = <RowData, RowId extends string = string>(
  repeatedData: string[][],
  selectionBounds: SelectionBounds,
  columnIds: string[],
  rowData: Row<RowData, RowId>[]
): OnPasteRow<RowData>[] =>
  repeatedData
    .map((pasteRow, rowIndex): OnPasteRow<RowData> | null => {
      const rowNode = rowData[selectionBounds.startRowIndex + rowIndex];

      if (!rowNode || !rowNode.data) {
        return null;
      }

      return {
        pasted: Object.fromEntries(
          pasteRow.map((value, colIndex) => [
            columnIds[selectionBounds.startColumnIndex + colIndex],
            value,
          ])
        ) as PastedData<RowData>,
        row: rowNode.data,
      };
    })
    .filter(isTruthy);

export type SelectionBounds = {
  startRowIndex: number;
  endRowIndex: number;
  startColumnIndex: number;
  endColumnIndex: number;
};

/**
 * Gets the bounds of the current cell selection
 */
export const getSelectionBounds = (
  cellRanges: CellRange[] | null,
  columnIds: string[]
): SelectionBounds | null => {
  if (!cellRanges || cellRanges.length === 0) {
    return null;
  }

  /**
   * If we want to support pasting data to multiple cell ranges,
   * we would need to handle multiple ranges here.
   * For now, we only support a single range.
   */
  const range = cellRanges[0];

  // Handle possibly undefined startRow and endRow
  if (!range.startRow || !range.endRow) {
    return null;
  }

  const startRowIndex = Math.min(
    range.startRow.rowIndex,
    range.endRow.rowIndex
  );
  const endRowIndex = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

  // Get column indices
  const firstColumnId = range.columns[0].getColId();
  const lastColumnId = range.columns[range.columns.length - 1].getColId();

  const startColumnIndex = columnIds.indexOf(firstColumnId);
  const endColumnIndex = columnIds.indexOf(lastColumnId);

  return {
    startRowIndex,
    endRowIndex,
    startColumnIndex: Math.min(startColumnIndex, endColumnIndex),
    endColumnIndex: Math.max(startColumnIndex, endColumnIndex),
  };
};
